import "@mui/x-data-grid/themeAugmentation";
import { createTheme } from "@mui/material/styles";
import { neutral, vibrant } from "./colors";

const colors = {
  white: "#fff",
  black: "#000",
  primary: "#fff",
  secondary: "#667085",
  error: "#bb241a",
  warning: "#ffc107",
  info: "#212636",
  success: "#15b79f",
  divider: vibrant[500],
  text: {
    primary: "#fff",
    secondary: "#fff",
  },
  background: {
    default: vibrant[900],
    paper: "#fff",
    drawer: vibrant[800],
  },
  shadow: {
    light:
      "rgba(0, 0, 0, 0.04) 0px 5px 22px 0px, rgba(0, 0, 0, 0.06) 0px 0px 0px 1px",
  },
  border: {
    default: "#dcdfe4",
  },
};

export const obsidianTheme = createTheme({
  components: {
    MuiFormControl: {
      styleOverrides: {
        root: {
          marginBottom: 0,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          marginBottom: 0,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { marginTop: 0, marginBottom: 0 },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: vibrant[900],
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          color: neutral[100],
          padding: "6px 10px",
          borderRadius: "6px",
          border: `1px solid ${vibrant[600]}`,
        },
        input: {
          padding: "0",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&:not(.MuiInputLabel-shrink)": {
            transform: "translate(14px, 10px) scale(1)",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[700],
          color: neutral[100],
          padding: "0",
          borderRadius: "14px !important",
          "& .MuiSelect-select": {
            padding: "14px 34px 14px 20px !important",
          },
        },
        icon: {
          color: "#fff",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "40px",
          padding: "2px 10px",
        },
        contained: {
          backgroundColor: vibrant[400],
          border: `1px solid ${vibrant[500]}`,
          color: "#fff",
          "&:hover": {
            backgroundColor: vibrant[600],
            borderColor: vibrant[700],
          },
          "&:disabled": {
            backgroundColor: vibrant[800],
            borderColor: vibrant[700],
            color: vibrant[300],
            cursor: "not-allowed",
          },
        },
        outlined: {
          border: `1px solid ${vibrant[700]}`,
          color: "#fff",
          "&:hover": {
            borderColor: vibrant[600],
          },
        },
        text: {
          "&.MuiButton-text": {
            border: "1px solid transparent",
            "&:hover": {
              border: `1px solid ${vibrant[500]}`,
            },
            "&:disabled": {
              color: vibrant[500],
            },
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: "#fff",
          fontSize: "14px",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[700],
          padding: "14px",
          marginLeft: 0,
          marginRight: 0,
          color: "#fff",
          minWidth: "260px",
          fontSize: "14px",
          display: "flex",
          flexDirection: "row-reverse",
          justifyContent: "space-between",
          borderRadius: "14px",
          span: {
            fontSize: "14px",
          },
          "&:hover": {
            backgroundColor: vibrant[600],
          },
          "& .Mui-checked": {
            color: "#fff !important",
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          padding: 0,
          color: vibrant[600],
          backgroundColor: vibrant[600],
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          color: colors.text.primary,
          border: `2px solid ${vibrant[500]}`,
          borderRadius: "14px",
          boxShadow: colors.shadow.light,
          padding: "14px",
          fontSize: "14px",
          fontWeight: 600,
          letterSpacing: "1px",

          "& .MuiAlert-icon": {
            color: vibrant[500],
          },
          "& .MuiAlert-message": {
            color: vibrant[100],
          },
          "& .MuiAlert-action": {
            color: vibrant[100],
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: vibrant[800],
          border: `2px solid ${vibrant[500]}`,
          borderRadius: "14px",
          boxShadow: colors.shadow.light,
          "& .MuiMenuItem-root": {
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: vibrant[700],
            },
          },
          "& .MuiTypography-root": {
            color: vibrant[100],
          },
          "& .MuiListItemIcon-root": {
            color: vibrant[100],
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[700],
          color: colors.text.primary,
          borderBottom: `1px solid ${colors.border.default}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: neutral[900],
          color: colors.text.primary,
          border: `1px solid ${vibrant[700]}`,
          borderWidth: "2px",
          borderStyle: "solid",
          borderRadius: "20px",
          boxShadow: colors.shadow.light,
          padding: "4px",

          "&.MuiPaper-root": {
            overflow: "visible",
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          border: `1px solid ${vibrant[700]}`,
          borderRadius: "10px",
          padding: "6px",
          "&:last-child": {
            paddingBottom: "6px",
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[500],
        },
      },
    },
    MuiCardMedia: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          marginBottom: "4px",
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "0",
          fontSize: "18px",
          paddingBottom: 0,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: colors.white,
        },
        subtitle1: {
          color: colors.text.secondary,
        },
        subtitle2: {
          color: colors.text.secondary,
        },
        body2: {
          color: colors.text.primary,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: "none",
          color: colors.white,
          fontWeight: "bold",
          transition: "color 0.15s ease",
          "&:hover": {
            color: vibrant[100],
            textDecoration: "underline",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "#fff",
          backgroundColor: vibrant[700],
          "& svg": {
            color: "#fff",
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          "& svg": {
            color: "#fff",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.drawer,
          color: "#fff",
          border: "none",
          paddingInline: "10px",
          paddingBlock: "18px",
          "& .MuiSvgIcon-root": {
            color: "#fff",
          },
          "& .MuiListItemIcon-root": {
            minWidth: "32px",
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0, 0, 0, 0.01)",
        },
        backdrop: {
          backdropFilter: "blur(8px)",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border: `1px solid ${vibrant[700]}`,
          "& .MuiDataGrid-main": {
            borderRadius: 0,
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: colors.background.default,
            color: "#fff",
            textTransform: "capitalize",
            letterSpacing: "1px",
            borderBottom: "none !important",
          },
          "& .MuiDataGrid-cell": {
            borderTop: `1px solid ${vibrant[700]}`,
            borderRight: `1px solid ${vibrant[700]}`,
          },
          "& .MuiDataGrid-columnSeparator": {
            color: vibrant[700],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `1px solid ${vibrant[700]}`,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          color: colors.text.primary,
          border: `1px solid ${vibrant[700]}`,
          borderRadius: "14px",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          border: `1px solid ${vibrant[700]}`,
          borderRadius: "14px",
          boxShadow: colors.shadow.light,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${vibrant[700]}`,
            color: colors.text.primary,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[700],
          "& .MuiTableCell-root": {
            backgroundColor: vibrant[700],
            color: colors.text.primary,
            fontWeight: 600,
            fontSize: "14px",
            letterSpacing: "0.5px",
            borderBottom: `2px solid ${vibrant[600]}`,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          "& .MuiTableCell-root": {
            color: colors.text.primary,
            backgroundColor: vibrant[800],
          },
          "& .MuiTableRow-root": {
            "&:hover": {
              backgroundColor: vibrant[700],
            },
            "&:last-child .MuiTableCell-root": {
              borderBottom: "none",
            },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[800],
          "&:hover": {
            backgroundColor: vibrant[700],
          },
          "&.Mui-selected": {
            backgroundColor: vibrant[600],
            "&:hover": {
              backgroundColor: vibrant[600],
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          color: colors.text.primary,
          borderBottom: `1px solid ${vibrant[700]}`,
          padding: "16px",
        },
        head: {
          backgroundColor: vibrant[700],
          color: colors.text.primary,
          fontWeight: 600,
          fontSize: "14px",
          letterSpacing: "0.5px",
          borderBottom: `2px solid ${vibrant[600]}`,
        },
        body: {
          color: colors.text.primary,
          backgroundColor: vibrant[800],
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: vibrant[600],
          color: colors.text.primary,
          fontWeight: 600,
          fontSize: "12px",
          height: "24px",
          "&:hover": {
            backgroundColor: vibrant[500],
          },
        },
        colorPrimary: {
          backgroundColor: vibrant[600],
          color: colors.text.primary,
          "&:hover": {
            backgroundColor: vibrant[500],
          },
        },
        colorSuccess: {
          backgroundColor: colors.success,
          color: colors.text.primary,
          "&:hover": {
            backgroundColor: "#12a68c",
          },
        },
        colorDefault: {
          backgroundColor: vibrant[700],
          color: colors.text.primary,
          "&:hover": {
            backgroundColor: vibrant[600],
          },
        },
      },
    },
  },
  palette: {
    primary: {
      main: colors.primary,
    },
    secondary: {
      main: colors.secondary,
    },
    error: {
      main: colors.error,
    },
    warning: {
      main: colors.warning,
    },
    info: {
      main: colors.info,
    },
    success: {
      main: colors.success,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
  },
  typography: {
    fontFamily: "Inter, -apple-system, 'system-ui', 'Segoe UI'",
    callout: {
      fontSize: "14px",
    },
    caption: {
      fontSize: "12px",
      marginBottom: "16px",
    },
  },
});
